import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import {
  NV_PATHS,
  NvAccessDialog,
  NvArrowRightIcon,
  NvCaseCard,
  NvCaseCover,
  NvErrorState,
  nvAgentDetailPath,
  nvCaseStudyPath,
} from "@/components/navigator";
import { useAccessGate } from "@/hooks/use-access-gate";
import { useRequest } from "@/hooks/use-request";
import { accessDenialOf } from "@/lib/access";
import { isNotFoundError } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { contactPath } from "@/services/navigator/contact-service";
import { getCaseStudy, type CaseBlock } from "@/services/navigator/case-study-service";
import { CASE_FORBIDDEN_MESSAGE, CASE_LOGIN_MESSAGE } from "./case-study-list-page";

// F02 Case 상세 (Figma 203:10125 전체 구성 · 203:9960 최소 구성, IA 133~143).
// 기본 섹션 과제 · 해결 방식 · 도입 효과 + 선택 섹션 개요 · 도입 과정, 좌측 고정 목차, 도입 에이전트 · 문의 CTA, 다른 Case 3개.
type SectionKey = "overview" | "challenge" | "solution" | "effect" | "process";
const SECTION_LABELS: Record<SectionKey, string> = {
  overview: "개요",
  challenge: "과제",
  solution: "해결 방식",
  effect: "도입 효과",
  process: "도입 과정",
};
const ORDER: SectionKey[] = ["overview", "challenge", "solution", "effect", "process"];

// 하단 [다른 Case Study 더보기]는 2차 반영 항목 (IA 144행 · Figma ver.7 1차 오픈 화면에는 없고 제안 17 상세 시안에만 있음).
// 1차 오픈에서는 숨겨요 (API · 컴포넌트는 그대로 둠) — 켜려면 CASE_OTHERS_ENABLED
const CASE_OTHERS_ENABLED = false;

// 도입 효과 수치 (제안 07 수치 규칙): 12자 이하만 대형 숫자, 길면 본문 크기로 자동 전환해요
const METRIC_BIG_MAX = 12;

// 문의 영역 제목의 에이전트명은 표시명만 (제안 17): 괄호 설명 · URL 제외
function agentDisplayName(name: string) {
  const cleaned = name
    .replace(/https?:\/\/\S+/gi, "")
    .replace(/\s*[(（][^)）]*[)）]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned || name.trim();
}

function CtaLink({ to, variant, children, state }: { to: string; variant: "primary" | "outline"; children: ReactNode; state?: unknown }) {
  return (
    <Link
      to={to}
      state={state}
      className={cn(
        // Figma widths: 271 (도입 문의) · 250 (에이전트 상세 보기), arrow at the right edge
        "inline-flex h-14 items-center justify-between gap-6 rounded-nv-control border border-nv-brand pl-[22px] pr-5 text-[16px] font-bold leading-[19px] tracking-[-0.01em] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nv-brand focus-visible:ring-offset-2",
        variant === "primary" ? "min-w-[271px] bg-nv-brand text-nv-text-inverse hover:bg-nv-brand-strong" : "min-w-[250px] font-medium text-nv-text-primary hover:bg-nv-brand-subtle",
      )}
    >
      {children}
      <NvArrowRightIcon className={variant === "outline" ? "text-nv-brand" : undefined} />
    </Link>
  );
}

function Blocks({ blocks }: { blocks: CaseBlock[] }) {
  return (
    <div className="flex flex-col">
      {blocks.map((block, i) => {
        if (block.type === "paragraph") {
          if (!block.text.trim()) return null;
          const prev = blocks[i - 1];
          return (
            <p key={i} className={cn("whitespace-pre-line text-[17px] leading-[32px] tracking-[-0.01em] text-nv-text-primary", prev?.type === "paragraph" && "mt-[18px]")}>
              {block.text}
            </p>
          );
        }
        if (block.type === "image") {
          return (
            <figure key={i} className="flex flex-col gap-[10px] py-9">
              <div className="overflow-hidden rounded-nv-card border border-nv-border">
                <img src={block.url} alt={block.caption ?? ""} className="block w-full" />
              </div>
              {block.caption && <figcaption className="text-[13px] leading-4 tracking-[-0.01em] text-nv-text-secondary">{block.caption}</figcaption>}
            </figure>
          );
        }
        if (block.type === "callout") {
          return (
            <div key={i} className="py-9">
              <aside className="flex flex-col gap-2 border-l-[3px] border-nv-brand bg-nv-bg-page py-6 pl-7 pr-7">
                {block.title && <p className="text-[16px] font-bold leading-[19px] tracking-[-0.01em] text-nv-text-primary">{block.title}</p>}
                <p className="whitespace-pre-line text-[16px] leading-[28.8px] tracking-[-0.01em] text-nv-text-secondary">{block.text}</p>
              </aside>
            </div>
          );
        }
        // 인용 블록은 Figma 시안이 없어 강조 박스와 같은 간격 · 글자로 그려요
        return (
          <div key={i} className="py-9">
            <blockquote className="border-l-[3px] border-nv-border pl-6">
              <p className="whitespace-pre-line text-[18px] leading-[30.6px] tracking-[-0.01em] text-nv-text-primary">“{block.text}”</p>
              {block.source && <footer className="mt-2 text-[13px] leading-4 text-nv-text-secondary">— {block.source}</footer>}
            </blockquote>
          </div>
        );
      })}
    </div>
  );
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col gap-[19px] pb-[26px]">
      <span aria-hidden className="h-[3px] w-7 bg-nv-brand" />
      <h2 className="text-[28px] font-normal leading-[36.4px] tracking-[-0.02em] text-nv-text-primary">{children}</h2>
    </div>
  );
}

export function NavigatorCaseStudyDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const gate = useAccessGate();
  const [active, setActive] = useState<SectionKey | null>(null);

  // One request per (case, entry source); a late response of another case never replaces this one
  const source = (location.state as { source?: string } | null)?.source ?? "case_study";
  const { data: detail, error, reload } = useRequest((signal) => getCaseStudy(id, source, signal), [id, source]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id, location.state]);

  // Why it did not load: refused by access level (popup) · does not exist (4xx) · could not load (5xx / network, retry)
  const denial = accessDenialOf(error);
  const missing = !!error && !denial && isNotFoundError(error);
  const failed = !!error && !denial && !missing;

  const sections = useMemo(() => {
    if (!detail) return [];
    const s = detail.sections;
    return ORDER.filter((key) => {
      if (key === "overview") return !!s.overview;
      if (key === "process") return !!s.process && s.process.steps.length > 0;
      return true;
    });
  }, [detail]);

  // 현재 섹션 강조 (IA 140): 화면 위쪽 1/3을 지나간 마지막 섹션
  useEffect(() => {
    if (!sections.length) return;
    const onScroll = () => {
      let current: SectionKey = sections[0];
      for (const key of sections) {
        const el = document.getElementById(`case-${key}`);
        if (el && el.getBoundingClientRect().top <= window.innerHeight / 3) current = key;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [sections]);

  if (!detail) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 py-40">
        {missing && (
          <>
            <p className="text-[15px] text-nv-text-secondary">Case Study를 찾을 수 없습니다.</p>
            <Link to={NV_PATHS.caseStudy} className="text-[15px] text-nv-brand-text underline">
              목록으로
            </Link>
          </>
        )}
        {failed && <NvErrorState error={error} onRetry={reload} />}
        <NvAccessDialog
          {...gate.dialogProps}
          open={denial !== null}
          reason={denial}
          onOpenChange={(open) => !open && navigate(NV_PATHS.caseStudy, { replace: true })}
          loginMessage={CASE_LOGIN_MESSAGE}
          forbiddenMessage={CASE_FORBIDDEN_MESSAGE}
        />
      </div>
    );
  }

  const agent = detail.agent;
  const agentLink = agent?.id && agent.available ? nvAgentDetailPath(agent.id) : null;
  const inquiry = contactPath({ source: "case_study", case_study_id: detail.id, agent_id: agentLink ? agent!.id! : undefined });
  const inquiryState = { from: location.pathname };
  const overview = detail.sections.overview;
  const customer = detail.customer_label;
  const scrollTo = (key: SectionKey) => {
    const el = document.getElementById(`case-${key}`);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 96, behavior: "smooth" });
  };

  return (
    <article>
      <section className="bg-nv-bg-page">
        <div className="mx-auto flex max-w-[1440px] items-center gap-16 px-[112px] pb-16 pt-10">
          <div className="flex w-[634px] shrink-0 flex-col">
            <nav aria-label="경로" className="flex items-center gap-2 text-[14px] leading-[17px] tracking-[-0.01em]">
              <Link to={NV_PATHS.caseStudy} className="font-medium text-nv-text-primary hover:underline">
                Case Study
              </Link>
              <span className="text-nv-text-secondary">/</span>
              <span className="text-nv-text-secondary">{detail.industry.label}</span>
            </nav>
            <h1 className="line-clamp-3 max-w-[620px] break-keep pt-5 text-[48px] font-normal leading-[55.7px] tracking-[-0.03em] text-nv-text-primary">{detail.title}</h1>
            {detail.summary && (
              <p className="max-w-[580px] whitespace-pre-line pt-[18px] text-[18px] leading-[30.6px] tracking-[-0.01em] text-nv-text-secondary">{detail.summary}</p>
            )}
            {/* 개요 섹션이 없으면(최소 구성) 고객사 · 적용 업무를 상단에 보여줘요 (203:9960) */}
            {!overview && (customer || detail.tasks.length > 0) && (
              <dl className="flex gap-9 pt-[26px]">
                {customer && (
                  <div className="flex flex-col gap-[2px]">
                    <dt className="text-[13px] leading-4 text-nv-text-secondary">고객사</dt>
                    <dd className="text-[15px] leading-[18px] text-nv-text-primary">{customer}</dd>
                  </div>
                )}
                {detail.tasks.length > 0 && (
                  <div className="flex flex-col gap-[2px]">
                    <dt className="text-[13px] leading-4 text-nv-text-secondary">적용 업무</dt>
                    <dd className="text-[15px] leading-[18px] text-nv-text-primary">{detail.tasks.join(", ")}</dd>
                  </div>
                )}
              </dl>
            )}
            <div className="flex gap-4 pt-[34px]">
              <CtaLink to={inquiry} variant="primary" state={inquiryState}>
                PwC와 함께 도입 문의하기
              </CtaLink>
              {agentLink && (
                <CtaLink to={agentLink} variant="outline">
                  에이전트 상세 보기
                </CtaLink>
              )}
            </div>
          </div>
          <div className="relative h-[389px] w-[518px] shrink-0 overflow-hidden rounded-nv-card bg-nv-bg-muted">
            <NvCaseCover url={detail.cover_image_url} industry={detail.industry} />
          </div>
        </div>
      </section>

      <div className="mx-auto flex max-w-[1440px] items-start gap-24 px-[112px] pt-[88px]">
        <nav aria-label="목차" className="sticky top-[96px] w-[220px] shrink-0 border-l-[3px] border-nv-border">
          {sections.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => scrollTo(key)}
              aria-current={active === key ? "true" : undefined}
              className={cn(
                "relative -ml-[3px] flex h-[42px] w-full items-center pl-[23px] text-left text-[15px] leading-[18px] tracking-[-0.01em]",
                active === key ? "text-nv-text-primary before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-nv-brand" : "text-nv-text-secondary hover:text-nv-text-primary",
              )}
            >
              {SECTION_LABELS[key]}
            </button>
          ))}
        </nav>

        <div className="flex w-[760px] min-w-0 flex-col gap-[104px]">
          {sections.map((key) => (
            <section key={key} id={`case-${key}`} aria-labelledby={`case-${key}-title`}>
              <SectionHeading>
                <span id={`case-${key}-title`}>{SECTION_LABELS[key]}</span>
              </SectionHeading>
              {key === "overview" && overview && (
                <dl className="border-t-2 border-nv-text-primary">
                  {[{ label: "고객사", value: customer ?? "-" }, ...overview.rows].map((row, i) => (
                    <div key={i} className="flex min-h-[49px] items-center gap-5 border-b border-nv-border py-[15px]">
                      <dt className="w-[150px] shrink-0 text-[14px] font-bold leading-[17px] tracking-[-0.01em] text-nv-text-primary">{row.label}</dt>
                      <dd className="text-[15px] leading-[18px] tracking-[-0.01em] text-nv-text-secondary">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {key === "effect" && detail.metrics.length > 0 && (
                <div className="flex gap-3 pb-9">
                  {detail.metrics.map((metric, i) => (
                    <div key={i} className="flex flex-1 flex-col rounded-nv-card bg-nv-bg-page px-6 pb-[22px] pt-6">
                      <span className="text-[15px] font-medium leading-[18px] tracking-[-0.01em] text-nv-text-secondary">{metric.label}</span>
                      <span
                        className={cn(
                          "pt-3 font-normal text-nv-text-primary",
                          metric.value.length <= METRIC_BIG_MAX
                            ? "text-[40px] leading-[42px] tracking-[-0.04em]"
                            : "text-[20px] leading-[28px] tracking-[-0.01em]",
                        )}
                      >
                        {metric.value}
                      </span>
                      {metric.comparison && <span className="pt-2 text-[15px] leading-[18px] tracking-[-0.01em] text-nv-text-primary">{metric.comparison}</span>}
                      {metric.basis && <span className="pt-3 text-[12px] leading-[14px] tracking-[-0.01em] text-nv-text-secondary">{metric.basis}</span>}
                    </div>
                  ))}
                </div>
              )}
              {(key === "challenge" || key === "solution" || key === "effect") && <Blocks blocks={detail.sections[key]?.blocks ?? []} />}
              {key === "process" && detail.sections.process && (
                <ol>
                  {detail.sections.process.steps.map((step, i, steps) => (
                    <li key={i} className={cn("relative flex gap-[14px]", i < steps.length - 1 && "pb-7")}>
                      <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full border border-nv-brand bg-nv-brand-subtle text-[12px] font-bold leading-[14px] text-nv-text-primary">
                        {i + 1}
                      </span>
                      {i < steps.length - 1 && <span aria-hidden className="absolute bottom-[-4px] left-3 top-8 w-[2px] bg-nv-bg-muted" />}
                      <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
                        <p className="text-[16px] font-medium leading-[19px] tracking-[-0.01em] text-nv-text-primary">{step.name}</p>
                        {step.period && <p className="text-[13px] leading-4 text-nv-text-secondary">{step.period}</p>}
                        {step.description && <p className="pt-1 text-[15px] leading-[26.2px] tracking-[-0.01em] text-nv-text-secondary">{step.description}</p>}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          ))}

          {agent && (
            <div className="relative flex items-center justify-between rounded-nv-card border border-nv-border py-4 pl-[22px] pr-5">
              <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] rounded-l-nv-card bg-nv-brand" />
              <div className="flex flex-col gap-[2px]">
                <span className="text-[12px] leading-[14px] tracking-[-0.01em] text-nv-text-secondary">도입 에이전트</span>
                <span className="flex items-center gap-2">
                  <span className="text-[18px] font-medium leading-[22px] tracking-[-0.01em] text-nv-text-primary">{agent.name}</span>
                  {agent.is_verified && (
                    <span className="rounded-nv-pill border border-nv-brand-text px-[6px] text-[12px] font-bold leading-[18px] text-nv-text-primary">Certified</span>
                  )}
                </span>
                {(agent.supplier_name || agent.category) && (
                  <span className="text-[13px] leading-4 tracking-[-0.01em] text-nv-text-secondary">
                    {[agent.supplier_name, agent.category].filter(Boolean).join(", ")}
                  </span>
                )}
              </div>
              {agentLink && (
                <Link to={agentLink} aria-label={`${agent.name} 에이전트 상세`} className="text-nv-brand-text after:absolute after:inset-0">
                  <NvArrowRightIcon />
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      <section className="mt-[120px] bg-nv-bg-page">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-4 px-[112px] pb-[120px] pt-[104px]">
          <h2 className="line-clamp-2 break-keep text-[48px] font-normal leading-[57.6px] tracking-[-0.03em] text-nv-text-primary">
            {agent ? `${agentDisplayName(agent.name)} 도입을 검토하고 있다면` : "이 사례와 같은 도입을 검토하고 있다면"}
          </h2>
          <p className="text-[17px] leading-[20.4px] tracking-[-0.01em] text-nv-text-secondary">사례에 쓰인 에이전트의 도입 조건과 범위를 PwC가 함께 확인합니다.</p>
          <div className="pt-6">
            <CtaLink to={inquiry} variant="primary" state={inquiryState}>
              PwC와 함께 도입 문의하기
            </CtaLink>
          </div>
        </div>
      </section>

      {CASE_OTHERS_ENABLED && detail.others.length > 0 && (
        <section className="mx-auto flex max-w-[1440px] flex-col gap-7 px-[112px] pb-[112px] pt-20">
          <div className="flex items-end justify-between">
            <h2 className="text-[28px] font-normal leading-[33.6px] tracking-[-0.02em] text-nv-text-primary">다른 Case Study 더보기</h2>
            <Link to={NV_PATHS.caseStudy} className="text-[15px] leading-[18px] tracking-[-0.01em] text-nv-brand-text hover:underline">
              목록에서 전체 보기
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-6">
            {detail.others.map((item) => (
              <NvCaseCard key={item.id} item={item} size="wide" realData to={nvCaseStudyPath(item.id)} />
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
